import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import SEOHead from "@/components/SEOHead";
import { Filter, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

function parseWeightLbs(value) {
  const match = String(value || "").match(/([\d.]+)\s*lbs?/i);
  return match ? Number(match[1]) : null;
}

function parsePanelAreaSqFt(value) {
  const text = String(value || "");
  if (!text || /folded|kit includes/i.test(text)) return null;
  const match = text.match(/([\d.]+)\s*[×x]\s*([\d.]+)/i);
  if (!match) return null;
  const length = Number(match[1]);
  const width = Number(match[2]);
  if (!length || !width) return null;
  return (length * width) / 144;
}

function calculatedMetrics(product) {
  const weight = parseWeightLbs(product.weight);
  const area = parsePanelAreaSqFt(product.dimensions);
  return {
    wattsPerLb: weight && product.wattage ? product.wattage / weight : null,
    wattsPerSqFt: area && product.wattage ? product.wattage / area : null,
  };
}

export default function Reviews() {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterForm, setFilterForm] = useState("all");

  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const panelTypes = ["all", ...new Set(products.map((p) => p.panel_type).filter(Boolean))];
  const formFactors = ["all", ...new Set(products.map((p) => p.form_factor).filter(Boolean))];

  const filtered = products.filter((p) => {
    const matchSearch = !search || `${p.product_name} ${p.brand} ${p.best_for_category}`.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === "all" || p.panel_type === filterType;
    const matchForm = filterForm === "all" || p.form_factor === filterForm;
    return matchSearch && matchType && matchForm;
  });

  const reviewProducts = products.filter((p) => p.review_slug);
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Solar Panel Reviews 2026",
    description: "In-depth solar panel reviews organized by category, wattage, and use case.",
    url: "https://highestefficiencysolarcell.com/reviews",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: reviewProducts.length,
      itemListElement: reviewProducts.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://highestefficiencysolarcell.com/review/${product.review_slug}`,
        name: product.product_name
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Solar Panel Reviews 2026 | High-Efficiency Reviews & Comparisons"
        description="In-depth solar panel reviews for high-efficiency monocrystalline, portable, RV, and bifacial panels. Filter by type, form factor, and use case."
        canonical="https://highestefficiencysolarcell.com/reviews"
        schema={schema}
      />

      <div className="pb-28 lg:pb-0">
        <div className="bg-gradient-to-br from-slate-950 to-slate-900 text-white py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-xs font-mono text-slate-400 mb-6">
              <Link to="/" className="hover:text-accent transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-slate-300">Solar Panel Reviews</span>
            </nav>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">Solar Panel Reviews</h1>
            <p className="text-slate-300 text-lg max-w-2xl mb-8 leading-relaxed">
              Specification-based solar panel reviews with calculated power-density and weight-efficiency comparisons. Product claims are separated from our own calculations, and purchase links go directly to product pages.
            </p>
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by name, brand, category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-10">
          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground mr-2">
              <Filter className="w-3.5 h-3.5" /> Cell type:
            </span>
            {panelTypes.map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                  filterType === type ? "bg-accent text-slate-950 font-semibold" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                }`}
              >
                {type === "all" ? "All Types" : type}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            <span className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground mr-2">
              <Filter className="w-3.5 h-3.5" /> Form factor:
            </span>
            {formFactors.map((form) => (
              <button
                key={form}
                onClick={() => setFilterForm(form)}
                className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                  filterForm === form ? "bg-accent text-slate-950 font-semibold" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                }`}
              >
                {form === "all" ? "All Forms" : form}
              </button>
            ))}
          </div>

          <AffiliateDisclosure />

          {products.length > 0 && (
            <section className="mt-8 mb-12">
              <div className="max-w-3xl mb-5">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-accent-text font-semibold">HESC Spec Lab</p>
                <h2 className="font-heading font-bold text-2xl mt-1 mb-2">Calculated Solar Panel Comparison Metrics</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  These figures are calculated by Highest Efficiency Solar Cell from the listed wattage, weight, and physical dimensions in our product database.
                  They are not laboratory test results. Watts per pound helps compare portability; watts per square foot helps compare rated power density when usable area is limited.
                  Folded dimensions and complete-kit dimensions are excluded from the area calculation because they do not represent deployed panel area.
                </p>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-border">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-secondary/60">
                    <tr>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Panel</th>
                      <th className="text-right px-4 py-3 font-heading font-semibold">Rated W</th>
                      <th className="text-right px-4 py-3 font-heading font-semibold">Listed efficiency</th>
                      <th className="text-right px-4 py-3 font-heading font-semibold">W / lb</th>
                      <th className="text-right px-4 py-3 font-heading font-semibold">W / sq ft</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products
                      .map((product) => ({ product, metrics: calculatedMetrics(product) }))
                      .sort((a, b) => (b.metrics.wattsPerSqFt || 0) - (a.metrics.wattsPerSqFt || 0))
                      .map(({ product, metrics }) => (
                        <tr key={product.id} className="border-t border-border">
                          <td className="px-4 py-3">
                            {product.review_slug ? (
                              <Link to={`/review/${product.review_slug}`} className="font-medium hover:text-accent hover:underline">
                                {product.product_name}
                              </Link>
                            ) : (
                              <span className="font-medium">{product.product_name}</span>
                            )}
                            <div className="text-xs text-muted-foreground mt-0.5">{product.form_factor} · {product.panel_type}</div>
                          </td>
                          <td className="text-right px-4 py-3 font-mono">{product.wattage ? `${product.wattage}W` : "—"}</td>
                          <td className="text-right px-4 py-3 font-mono">{product.claimed_efficiency || "—"}</td>
                          <td className="text-right px-4 py-3 font-mono">{metrics.wattsPerLb ? metrics.wattsPerLb.toFixed(1) : "—"}</td>
                          <td className="text-right px-4 py-3 font-mono">{metrics.wattsPerSqFt ? metrics.wattsPerSqFt.toFixed(1) : "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                “Listed efficiency” is a stored manufacturer/listing specification and is not independently measured by this site. Verify current manufacturer documentation before designing a system.
              </p>
            </section>
          )}

          {filtered.length > 0 ? (
            <>
              <p className="text-xs font-mono text-muted-foreground mb-5">{filtered.length} panel{filtered.length !== 1 ? "s" : ""} shown</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </>
          ) : (
            <div className="bg-secondary/50 rounded-xl p-12 text-center">
              <p className="text-muted-foreground">
                {search || filterType !== "all" || filterForm !== "all"
                  ? "No panels match your filters. Try adjusting your search."
                  : "Reviews are being prepared. Check back soon."}
              </p>
            </div>
          )}

          {/* Static editorial content for SEO / AdSense value */}
          <div className="mt-14 max-w-3xl">
            <h2 className="font-heading font-bold text-2xl mb-4">Solar Panel Categories Explained</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Our solar panel catalog is organized by best-use category so you can quickly find the right panel
                for your specific application. Here is what each category means and who it is designed for.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: "High-Efficiency Comparison", desc: "Panels with strong listed efficiency or power density. Compare watts per square foot, physical dimensions, temperature behavior, warranty terms, and electrical fit rather than relying on one headline percentage." },
                  { label: "Portable Solar Panels", desc: "Foldable or deployable panels intended for temporary use. Compare deployed size, folded size, weight, connectors, weather guidance, and the PV input limits of the power station you plan to charge." },
                  { label: "RV Solar Panels", desc: "Panels considered for vehicle-mounted arrays. Roof dimensions, shade from rooftop equipment, weight, mounting method, controller limits, wiring, and current manufacturer installation guidance all matter." },
                  { label: "Camping Solar Panels", desc: "Portable panels for campsite charging. Compare the daily energy target, carrying weight, setup method, connector compatibility, cable length, and current weather-resistance documentation." },
                  { label: "Flexible Solar Panels", desc: "Thin, low-weight modules intended for installations where mild curvature or low profile matters. Verify the permitted bend radius, mounting method, heat management, surface compatibility, and warranty for the exact model." },
                  { label: "Bifacial Solar Panels", desc: "Dual-sided panels that can use reflected light on the rear face. They are most interesting when mounting clearance and the surrounding surface give the rear side useful light; actual additional energy is site-dependent." },
                  { label: "Solar Panel Kits", desc: "Bundled solar components sold together. Check exactly what is included, controller limits, battery requirements, mounting hardware, wiring, and whether the bundle matches your planned system." },
                  { label: "Power-Station Solar", desc: "Panels compared for portable power stations. Compatibility depends on the exact station's PV voltage, current, power, connector, and polarity requirements, not brand name alone." },
                  { label: "Value-Oriented Comparison", desc: "Panels evaluated for practical specifications without assuming a current price advantage. Check today's retailer price separately, then compare electrical fit, dimensions, listed efficiency, and warranty terms." },
                  { label: "Premium Panel Comparison", desc: "Higher-end panels compared by listed efficiency, warranty terms, construction, and space efficiency. Verify the current manufacturer warranty and degradation specification for the exact model." },
                ].map((item, i) => (
                  <div key={i} className="bg-secondary/50 border border-border rounded-xl p-4">
                    <h3 className="font-heading font-semibold text-foreground text-sm mb-1">{item.label}</h3>
                    <p className="text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/solar-guides" className="block bg-secondary border border-border rounded-2xl p-6 hover:border-accent/40 hover:shadow-lg transition-all group">
              <h3 className="font-heading font-semibold mb-1 group-hover:text-accent transition-colors">Browse All Solar Guides →</h3>
              <p className="text-sm text-muted-foreground">Buyer's guides, sizing calculators, and how-to articles.</p>
            </Link>
            <Link to="/solar-panel-calculator" className="block bg-secondary border border-border rounded-2xl p-6 hover:border-accent/40 hover:shadow-lg transition-all group">
              <h3 className="font-heading font-semibold mb-1 group-hover:text-accent transition-colors">Use the Solar Calculator →</h3>
              <p className="text-sm text-muted-foreground">Calculate exactly how many watts you need.</p>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}